import { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { Badge } from './Badge';
import { Button } from './Button';
import { Card } from './Card';
import { FormErrorBanner } from './FormErrorBanner';
import { CurrencyInput } from './CurrencyInput';
import { TextInput } from './TextInput';
import { formatCurrency } from '../lib/utils';
import { webPrivacyProps } from '../lib/privacy';

type AssetType = 'home' | 'vehicle' | 'rental_property' | 'other';
type ValuationSource = 'manual' | 'redfin' | 'zillow' | 'marketcheck' | 'nhtsa';

export type AssetFormValuation = {
  amount: number;
  source: ValuationSource;
  confidence: number | null;
  updatedAt: string;
  sourceUrl: string | null;
  metadata: Record<string, unknown>;
};

export type AssetFormInput = {
  name: string;
  assetType: AssetType;
  description?: string;
  annualGrowthRate?: number;
  address?: {
    street: string | null;
    city: string | null;
    region: string | null;
    postalCode: string | null;
    country: string | null;
  };
  vehicle?: {
    vin: string | null;
    mileage: number | null;
    year: number | null;
    make: string | null;
    model: string | null;
    trim: string | null;
  };
  manualValue?: number | null;
  valuation?: AssetFormValuation;
};

type AssetFormProps = {
  onEstimateValue: (input: {
    assetType: AssetType;
    address?: AssetFormInput['address'];
    vehicle?: AssetFormInput['vehicle'];
  }) => Promise<AssetFormValuation | null>;
  onSave: (input: AssetFormInput) => Promise<void>;
  open?: boolean;
  onClose?: () => void;
  saving?: boolean;
  estimating?: boolean;
  addButtonLabel?: string;
};

const ASSET_TYPES: Array<{ value: AssetType; label: string; hint: string }> = [
  { value: 'home', label: 'Home', hint: 'Primary residence or vacation home' },
  { value: 'vehicle', label: 'Vehicle', hint: 'Car, truck, motorcycle, camper' },
  { value: 'rental_property', label: 'Rental', hint: 'Income property or second unit' },
  { value: 'other', label: 'Other', hint: 'Boat, land, collectibles, private business stake' },
];

function parseOptionalNumber(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }

  const parsed = Number(trimmed.replaceAll(',', ''));
  return Number.isFinite(parsed) ? parsed : null;
}

function formatEditableAmount(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

function formatEditablePercent(value: number): string {
  return (value * 100).toFixed(1);
}

function getDefaultAnnualGrowthRate(assetType: AssetType): number {
  switch (assetType) {
    case 'home':
      return 0.03;
    case 'rental_property':
      return 0.0325;
    case 'vehicle':
      return -0.12;
    default:
      return 0.01;
  }
}

function isValidVin(value: string): boolean {
  return /^[A-HJ-NPR-Z0-9]{17}$/.test(value);
}

export function AssetForm({
  onEstimateValue,
  onSave,
  saving = false,
  estimating = false,
  addButtonLabel = '+ Add an asset',
  open,
  onClose,
}: AssetFormProps) {
  const isControlled = open !== undefined;
  const [internalAdding, setInternalAdding] = useState(false);
  const adding = isControlled ? open : internalAdding;
  const setAdding = (next: boolean) => {
    if (isControlled) {
      if (!next) onClose?.();
    } else {
      setInternalAdding(next);
    }
  };
  const [assetType, setAssetType] = useState<AssetType>('home');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('US');
  const [vin, setVin] = useState('');
  const [mileage, setMileage] = useState('');
  const [year, setYear] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [trim, setTrim] = useState('');
  const [manualValue, setManualValue] = useState('');
  const [annualGrowthRate, setAnnualGrowthRate] = useState(
    formatEditablePercent(getDefaultAnnualGrowthRate('home')),
  );
  const [valuation, setValuation] = useState<AssetFormValuation | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setAdding(false);
    setAssetType('home');
    setName('');
    setDescription('');
    setStreet('');
    setCity('');
    setRegion('');
    setPostalCode('');
    setCountry('US');
    setVin('');
    setMileage('');
    setYear('');
    setMake('');
    setModel('');
    setTrim('');
    setManualValue('');
    setAnnualGrowthRate(formatEditablePercent(getDefaultAnnualGrowthRate('home')));
    setValuation(null);
    setError(null);
  };

  const buildInput = (options: { requireValue: boolean }): AssetFormInput | null => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Please give this asset a name.');
      return null;
    }

    const parsedManualValue = parseOptionalNumber(manualValue);
    if (parsedManualValue === null || parsedManualValue === 0) {
      setError('Manual value must be a positive number if you enter one.');
      return null;
    }

    const parsedAnnualGrowthRate = parseOptionalNumber(annualGrowthRate);
    if (parsedAnnualGrowthRate === null) {
      setError('Annual growth rate must be a valid percentage.');
      return null;
    }

    const annualGrowthRateDecimal =
      (parsedAnnualGrowthRate ?? getDefaultAnnualGrowthRate(assetType) * 100) / 100;
    if (annualGrowthRateDecimal <= -1 || annualGrowthRateDecimal > 1) {
      setError('Annual growth rate must stay between -100% and 100%.');
      return null;
    }

    const address =
      assetType === 'home' || assetType === 'rental_property' || assetType === 'vehicle'
        ? {
            street: street.trim() || null,
            city: city.trim() || null,
            region: region.trim() || null,
            postalCode: postalCode.trim() || null,
            country: country.trim() || null,
          }
        : undefined;

    if ((assetType === 'home' || assetType === 'rental_property')
      && (!address?.street || !address.city || !address.region || !address.postalCode)
    ) {
      setError('Homes and rentals need a full address.');
      return null;
    }

    const parsedMileage = parseOptionalNumber(mileage);
    if (parsedMileage === null) {
      setError('Mileage must be a valid number.');
      return null;
    }

    const parsedYear = parseOptionalNumber(year);
    if (parsedYear === null) {
      setError('Vehicle year must be a valid number.');
      return null;
    }

    const vehicle =
      assetType === 'vehicle'
        ? {
            vin: vin.trim().toUpperCase() || null,
            mileage: parsedMileage ?? null,
            year: parsedYear ?? null,
            make: make.trim() || null,
            model: model.trim() || null,
            trim: trim.trim() || null,
          }
        : undefined;

    if (assetType === 'vehicle' && !vehicle?.vin) {
      setError('Vehicles need a VIN.');
      return null;
    }

    if (assetType === 'vehicle' && vehicle?.vin && !isValidVin(vehicle.vin)) {
      setError('Enter a valid 17-character VIN.');
      return null;
    }

    if (options.requireValue && !valuation && parsedManualValue == null) {
      setError('Add a manual value or run an estimate before saving.');
      return null;
    }

    return {
      name: trimmedName,
      assetType,
      ...(description.trim() ? { description: description.trim() } : {}),
      annualGrowthRate: annualGrowthRateDecimal,
      ...(address ? { address } : {}),
      ...(vehicle ? { vehicle } : {}),
      ...(parsedManualValue !== undefined ? { manualValue: parsedManualValue ?? null } : {}),
      ...(valuation ? { valuation } : {}),
    };
  };

  const buildEstimateInput = (): {
    assetType: AssetType;
    address?: AssetFormInput['address'];
    vehicle?: AssetFormInput['vehicle'];
  } | null => {
    const address =
      assetType === 'home' || assetType === 'rental_property' || assetType === 'vehicle'
        ? {
            street: street.trim() || null,
            city: city.trim() || null,
            region: region.trim() || null,
            postalCode: postalCode.trim() || null,
            country: country.trim() || null,
          }
        : undefined;

    if ((assetType === 'home' || assetType === 'rental_property')
      && (!address?.street || !address.city || !address.region || !address.postalCode)
    ) {
      setError('Homes and rentals need a full address.');
      return null;
    }

    const parsedMileage = parseOptionalNumber(mileage);
    if (parsedMileage === null) {
      setError('Mileage must be a valid number.');
      return null;
    }

    const parsedYear = parseOptionalNumber(year);
    if (parsedYear === null) {
      setError('Vehicle year must be a valid number.');
      return null;
    }

    const vehicle =
      assetType === 'vehicle'
        ? {
            vin: vin.trim().toUpperCase() || null,
            mileage: parsedMileage ?? null,
            year: parsedYear ?? null,
            make: make.trim() || null,
            model: model.trim() || null,
            trim: trim.trim() || null,
          }
        : undefined;

    if (assetType === 'vehicle' && !vehicle?.vin) {
      setError('Vehicles need a VIN.');
      return null;
    }

    if (assetType === 'vehicle' && vehicle?.vin && !isValidVin(vehicle.vin)) {
      setError('Enter a valid 17-character VIN.');
      return null;
    }

    return {
      assetType,
      ...(address ? { address } : {}),
      ...(vehicle ? { vehicle } : {}),
    };
  };

  const handleEstimate = async () => {
    setError(null);
    const input = buildEstimateInput();
    if (!input) {
      return;
    }

    if (assetType === 'other') {
      setError('Other assets need a manual value for now.');
      return;
    }

    const result = await onEstimateValue(input);

    if (!result) {
      setValuation(null);
      setError(
        assetType === 'vehicle'
          ? 'No automatic vehicle estimate came back. You can still save with a manual value.'
          : 'No home estimate came back. You can still save with a manual value.',
      );
      return;
    }

    setValuation(result);
    if (!manualValue.trim()) {
      setManualValue(formatEditableAmount(result.amount));
    }
  };

  const handleSave = async () => {
    setError(null);
    const input = buildInput({ requireValue: true });
    if (!input) {
      return;
    }

    try {
      await onSave(input);
      reset();
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : 'Could not save this asset. Please try again.',
      );
    }
  };

  if (!adding) {
    if (isControlled) return null;
    return (
      <Button variant="ghost" onPress={() => setAdding(true)}>
        {addButtonLabel}
      </Button>
    );
  }

  return (
    <Card>
      <Text className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Add a major asset</Text>

      <FormErrorBanner error={error} onDismiss={() => setError(null)} />
      {error && <View className="mb-4" />}

      <Text className="text-xs text-gray-500 dark:text-gray-400 mb-2">Asset type</Text>
      <View className="flex-row flex-wrap gap-2 mb-4">
        {ASSET_TYPES.map((option) => (
          <Pressable
            key={option.value}
            className={`rounded-xl border px-4 py-3 ${
              assetType === option.value
                ? 'border-green-600 bg-green-50 dark:bg-green-900/30'
                : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700'
            }`}
            onPress={() => {
              setAssetType(option.value);
              setAnnualGrowthRate(formatEditablePercent(getDefaultAnnualGrowthRate(option.value)));
              setError(null);
              setValuation(null);
            }}
          >
            <Text
              className={`text-sm font-semibold ${
                assetType === option.value ? 'text-green-800 dark:text-green-300' : 'text-gray-800 dark:text-gray-200'
              }`}
            >
              {option.label}
            </Text>
            <Text
              className={`mt-1 w-32 text-xs ${
                assetType === option.value ? 'text-green-700/80 dark:text-green-400/80' : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {option.hint}
            </Text>
          </Pressable>
        ))}
      </View>

      <TextInput
        label="Name"
        value={name}
        onChangeText={(text) => {
          setName(text);
          setError(null);
        }}
        placeholder={
          assetType === 'home'
            ? 'Primary home'
            : assetType === 'vehicle'
              ? 'Family car'
              : assetType === 'rental_property'
                ? 'Duplex in Raleigh'
                : 'Lake lot, business interest, boat...'
        }
        containerClassName="mb-3"
      />

      <TextInput
        label="Notes (optional)"
        value={description}
        onChangeText={setDescription}
        placeholder="Anything important about this asset"
        containerClassName="mb-4"
      />

      {(assetType === 'home' || assetType === 'rental_property') && (
        <>
          <TextInput
            label="Address"
            value={street}
            onChangeText={setStreet}
            placeholder="Street"
            containerClassName="mb-3"
          />
          <View className="flex-row gap-3 mb-3">
            <TextInput
              value={city}
              onChangeText={setCity}
              placeholder="City"
              containerClassName="flex-1"
            />
            <TextInput
              value={region}
              onChangeText={setRegion}
              placeholder="State"
              autoCapitalize="characters"
              containerClassName="w-24"
            />
          </View>
          <View className="flex-row gap-3 mb-4">
            <TextInput
              value={postalCode}
              onChangeText={setPostalCode}
              placeholder="ZIP"
              keyboardType="numeric"
              containerClassName="flex-1"
            />
            <TextInput
              value={country}
              onChangeText={setCountry}
              placeholder="Country"
              autoCapitalize="characters"
              containerClassName="w-24"
            />
          </View>
        </>
      )}

      {assetType === 'vehicle' && (
        <>
          <Text className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Location for pricing</Text>
          <View className="flex-row gap-3 mb-3">
            <TextInput
              value={city}
              onChangeText={setCity}
              placeholder="City"
              containerClassName="flex-1"
            />
            <TextInput
              value={region}
              onChangeText={setRegion}
              placeholder="State"
              autoCapitalize="characters"
              containerClassName="w-24"
            />
          </View>
          <View className="flex-row gap-3 mb-4">
            <TextInput
              value={postalCode}
              onChangeText={setPostalCode}
              placeholder="ZIP"
              keyboardType="numeric"
              containerClassName="flex-1"
            />
            <TextInput
              value={country}
              onChangeText={setCountry}
              placeholder="Country"
              autoCapitalize="characters"
              containerClassName="w-24"
            />
          </View>
        </>
      )}

      {assetType === 'vehicle' && (
        <>
          <TextInput
            label="VIN and vehicle details"
            value={vin}
            onChangeText={setVin}
            placeholder="17-character VIN"
            autoCapitalize="characters"
            autoCorrect={false}
            containerClassName="mb-3"
          />
          <View className="flex-row gap-3 mb-3">
            <TextInput
              value={mileage}
              onChangeText={setMileage}
              placeholder="Mileage"
              keyboardType="numeric"
              containerClassName="flex-1"
            />
            <TextInput
              value={year}
              onChangeText={setYear}
              placeholder="Year"
              keyboardType="numeric"
              containerClassName="w-28"
            />
          </View>
          <View className="flex-row gap-3 mb-4">
            <TextInput
              value={make}
              onChangeText={setMake}
              placeholder="Make"
              containerClassName="flex-1"
            />
            <TextInput
              value={model}
              onChangeText={setModel}
              placeholder="Model"
              containerClassName="flex-1"
            />
          </View>
          <TextInput
            value={trim}
            onChangeText={setTrim}
            placeholder="Trim (optional)"
            containerClassName="mb-4"
          />
        </>
      )}

      <CurrencyInput
        label="Estimated value"
        value={manualValue}
        onChangeText={(text) => {
          setManualValue(text);
          setError(null);
        }}
        placeholder="Auto-filled from lookup or enter manually"
        containerClassName="mb-3"
      />

      <TextInput
        label="Annual growth rate (%)"
        value={annualGrowthRate}
        onChangeText={(text) => {
          setAnnualGrowthRate(text);
          setError(null);
        }}
        placeholder={formatEditablePercent(getDefaultAnnualGrowthRate(assetType))}
        keyboardType="numeric"
        containerClassName="mb-4"
      />

      {assetType !== 'other' && (
        <View className="rounded-2xl bg-emerald-50 dark:bg-emerald-900/30 px-4 py-4 mb-4">
          <View className="flex-row items-center justify-between gap-3">
            <View className="flex-1">
              <Text className="text-sm font-semibold text-emerald-900 dark:text-emerald-300">
                Automatic estimate
              </Text>
              <Text className="text-xs text-emerald-800/80 dark:text-emerald-400/80 mt-1">
                {assetType === 'vehicle'
                  ? 'We use your VIN plus mileage when available. If pricing is unavailable, enter a value manually.'
                  : 'Homes use Zillow when available. If pricing is unavailable, enter a value manually.'}
              </Text>
            </View>
            <Button size="sm" onPress={handleEstimate} loading={estimating}>
              Look up
            </Button>
          </View>
        </View>
      )}

      {valuation && (
        <View className="rounded-2xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30 px-4 py-4 mb-4">
          <View className="flex-row items-center justify-between gap-3">
            <Text
              className="text-lg font-bold text-green-900 dark:text-green-300"
              {...webPrivacyProps('financial')}
            >
              {formatCurrency(valuation.amount, { decimals: 0 })}
            </Text>
            <Badge
              label={valuation.source === 'manual' ? 'Manual' : valuation.source.toUpperCase()}
              variant="green"
            />
          </View>
          <Text className="text-xs text-green-800/80 dark:text-green-400/80 mt-1">
            {valuation.confidence != null
              ? `Confidence ${Math.round(valuation.confidence * 100)}%`
              : 'Manual value'}
          </Text>
        </View>
      )}

      <View className="flex-row gap-2">
        <Button className="flex-1" onPress={handleSave} loading={saving}>
          Save asset
        </Button>
        <Button variant="ghost" onPress={reset}>
          Cancel
        </Button>
      </View>
    </Card>
  );
}
